/**
 * Shared loader for a cluster's image embeddings (+ urls), used by ClusterPairExplorer and
 * MultiClusterScatterModal so neither needs a `loadClusterItems` prop.
 *
 * Results are cached process-wide by cluster id (a module-level Map), so a component that loads a
 * pair reuses anything a sibling already fetched — and callers can warm the cache ahead of time via
 * prefetchClusterItems (e.g. the dataset-review queue pre-loading upcoming pairs for instant nav).
 * Vectors are L2-normalized so client-side dot products are exact cosine. A failed load is evicted
 * so a later attempt can retry instead of caching the rejection.
 */
const FUSEBOX_MODEL_EMBEDDINGS_SERVICE = 'services/data/fusebox-model-embeddings';
const cache = new Map(); // clusterId -> Promise<Array<{imgId, imgUrl, clusterId, vehicle, vector}>>

/** L2-normalize so client-side dot products are exact cosine (stored vectors are raw). */
function normalizeVector(vector) {
  let sumSq = 0;
  for (const x of vector) sumSq += x * x;
  const norm = Math.sqrt(sumSq) || 1;
  return vector.map((x) => x / norm);
}

/**
 * Embeddings of every image of a cluster, normalized and cached.
 * @param {{service: Function}} page  anything exposing `.service(path)` (the LegoAdminPageContext page, or a LivePage)
 * @param {string|number} clusterId
 * @returns {Promise<Array<{imgId, imgUrl, clusterId, vehicle, vector}>>}
 */
export function loadClusterItems(page, clusterId) {
  const key = String(clusterId);
  if (!cache.has(key)) {
    const promise = page.service(FUSEBOX_MODEL_EMBEDDINGS_SERVICE)
      .find({ query: { clusterId: key } })
      .then((res) => (res?.items || [])
        .filter((it) => it.imgUrl && Array.isArray(it.embedding) && it.embedding.length)
        .map((it) => ({
          imgId: String(it.imgId),
          imgUrl: it.imgUrl,
          clusterId: String(it.clusterId),
          vehicle: it.vehicle || null,
          vector: normalizeVector(it.embedding),
        })))
      .catch((err) => {
        cache.delete(key); // let a later attempt retry instead of caching the failure
        throw err;
      });
    cache.set(key, promise);
  }
  return cache.get(key);
}

/** Fire-and-forget warm of the cache for a cluster (errors swallowed). */
export function prefetchClusterItems(page, clusterId) {
  loadClusterItems(page, clusterId).catch(() => {});
}
